import styled from '@emotion/styled';
import React from 'react';
import Image from '@base/FutureImage';
import getTextColor from 'aquilla/core/shared/utils/getTextColor';
import { Typography } from 'aquilla/core/atoms/Typography';
import { Fonts, Spacings } from '@styles/index';
import { Divider } from 'aquilla/core/atoms/Divider';
import { Play } from 'aquilla/icons/actions/Play';
import { getSpecialInfoString } from '@models/Item';
import { keyframes } from '@emotion/react';
import { nFormatter } from '@utils/textHelper';
import ThumbnailBg from './ThumbnailBg';
import { Preview, getDuration } from './utils';

const PreviewContainer = styled.div`
  height: 521px;
  width: 100%;
  position: relative;
  overflow: hidden;
  cursor: ${({ $isUpcoming }) => ($isUpcoming ? 'default' : 'pointer')};

  img {
    width: auto;
    height: 90%;
    position: absolute;
    left: ${Spacings.SPACING_12B};
    bottom: -16px;
  }

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

const PulseAnimation = (width) => keyframes`
  50% {
    box-shadow: 0 0 0 ${width} rgba(235, 87, 87, 0.25);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(235, 87, 87, 0);
  }
`;

const PlayIconContainer = styled.div`
  height: ${Spacings.SPACING_24B};
  width: ${Spacings.SPACING_24B};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  opacity: 0.9;
  padding: ${Spacings.SPACING_8B};
  cursor: pointer;
  animation: ${() => PulseAnimation(Spacings.SPACING_4B)}
    ${({ $isLive }) => ($isLive ? '2s' : '0s')} infinite;
  transition: 0.5s;
`;

const DetailsContainer = styled.div`
  position: absolute;
  top: ${({ $isUpcoming }) => ($isUpcoming ? '25%' : '50%')};
  right: ${Spacings.SPACING_16B};
  transform: ${({ $isUpcoming }) =>
    $isUpcoming ? 'translateY(0%)' : 'translateY(-50%)'};
  width: 504px;
`;

const Header = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 2;
`;

const TagContainer = styled.div`
  position: relative;
  display: inline-block;
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
  margin-bottom: ${Spacings.SPACING_3B};
  color: ${({ $color }) => $color};
`;
const TagBg = styled.div`
  position: absolute;
  opacity: 0.15;
  background-color: ${({ $color }) => $color};
  width: 100%;
  height: 100%;
  color: ${({ $color }) => $color};
`;
const Tag = styled(Typography)`
  padding: ${Spacings.SPACING_5} ${Spacings.SPACING_10};
  text-transform: uppercase;
  color: inherit;
  font-size: 18px;
`;

const StyledDivider = styled(Divider)`
  border-color: var(--color-text-primary);
  margin: ${Spacings.SPACING_4B} 0;
  opacity: 0.2;
  width: 98%;
`;

const ClassInfo = styled(Typography)``;
const LiveTag = styled.div`
  background-color: var(--color-i-red);
  color: var(--color-i-white);
  text-transform: uppercase;
  border-radius: ${Spacings.SPACING_1B};
  position: absolute;
  left: 30px;
  bottom: 30px;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
  ::before {
    content: 'live';
    font-size: ${Fonts.H4};
    font-weight: ${Fonts.HEAVY};
  }
`;

const DurationTag = styled.div`
  background-color: var(--color-overlay);
  color: var(--color-i-white);
  border-radius: ${Spacings.SPACING_1B};
  position: absolute;
  right: 30px;
  bottom: 30px;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
`;

const StyledPreview = styled(Preview)`
  display: none;
  @media only screen and (max-width: 768px) {
    display: block;

    img {
      width: 100%;
    }
  }
`;

const PreviewNew = ({
  author,
  topicGroupName,
  classInfo,
  courseInfo,
  isLive,
  isUpcoming,
  onPlay,
  downloadApp,
  isClassHappenedAndNotRecorded
}) => {
  const learners = classInfo.attendance || 0;
  let subLabel = getSpecialInfoString(classInfo, false);
  if (isLive && learners > 10)
    subLabel += ` • ${nFormatter(learners || 0, 1)} watching`;
  else if (!isUpcoming && learners > 100)
    subLabel += ` • ${nFormatter(learners || 0, 1)} views`;

  const duration = getDuration(
    classInfo.lessonDuration || classInfo.videoDuration || 0
  );

  const { isSpecial } = classInfo.programme || {};
  const color = getTextColor(topicGroupName[0]);
  const handleClassPlay = () => {
    const vw = document.documentElement.clientWidth;
    const lpss = 'In page player - control';
    if (vw <= 768) downloadApp();
    else onPlay(lpss);
  };
  const avatar = author.avatarV2 || author.oldAvatar;
  return (
    <>
      <StyledPreview
        url={courseInfo.coverPhoto}
        color={color}
        isLive={isLive}
        handlePlayCTA={onPlay}
        isMobile
      />
      <PreviewContainer onClick={handleClassPlay} $isUpcoming={isUpcoming}>
        <ThumbnailBg color={color} hasEducatorImg={avatar} />
        {avatar && (
          <Image src={avatar} alt="" height="521" width="1136" priority />
        )}
        <DetailsContainer $isUpcoming={isUpcoming}>
          <TagContainer $color={color}>
            <TagBg $color={color} />
            <Tag variant="h4">
              {topicGroupName} {isSpecial ? ' • free class' : ''}
            </Tag>
          </TagContainer>
          <Header variant="h1" color="var(--color-text-primary)">
            {classInfo.name}
          </Header>
          <StyledDivider />
          <ClassInfo variant="h4">{subLabel}</ClassInfo>
        </DetailsContainer>
        {!isUpcoming && !isClassHappenedAndNotRecorded && (
          <PlayIconContainer
            $color={isLive ? 'var(--color-i-red)' : color}
            $isLive={isLive}
          >
            <Play color="var(--color-i-white)" width={28} height={28} />
          </PlayIconContainer>
        )}
        {isLive && <LiveTag />}
        {!(isLive || isUpcoming) && !isClassHappenedAndNotRecorded && (
          <DurationTag>
            <Typography variant="h4" color="var(--color-i-white)">
              {duration}
            </Typography>
          </DurationTag>
        )}
      </PreviewContainer>
    </>
  );
};

export default PreviewNew;
